import React, { useCallback, useRef, useState } from 'react';
import { Loader2, RefreshCw, Sparkles } from 'lucide-react';
import api from '../services/api';
import { Button } from './ui/button';

/**
 * Shared inline AI name comparison (PSB + delivery documents). Advisory only:
 * a verdict never selects, learns, creates or posts anything. Requests are
 * explicit (one per Suggest / "Compare with AI" action), cached server-side,
 * and stale replies (inputs changed since the request) are discarded.
 */
const AI_TIMEOUT_MS = 45000;

export function useAiCompare() {
  const [byKey, setByKey] = useState({});
  const seq = useRef({});

  const run = useCallback(async (key, payload) => {
    const reqId = (seq.current[key] || 0) + 1;
    seq.current[key] = reqId;
    const ids = payload.candidate_ids.join(',');
    setByKey((prev) => ({ ...prev, [key]: { status: 'loading', ids } }));
    try {
      const { data } = await api.post('/matching/ai-compare', payload, { timeout: AI_TIMEOUT_MS });
      if (seq.current[key] !== reqId) return;
      setByKey((prev) => ({ ...prev, [key]: { status: data.status, ids, data } }));
    } catch (err) {
      if (seq.current[key] !== reqId) return;
      const msg = err?.code === 'ECONNABORTED' ? 'AI comparison timed out' : (err?.response?.data?.detail || 'AI comparison failed');
      setByKey((prev) => ({ ...prev, [key]: { status: 'error', ids, data: { message: typeof msg === 'string' ? msg : 'AI comparison failed' } } }));
    }
  }, []);

  return { byKey, run };
}

const IDENTITY_TEXT = { same: 'AI: same product', different: 'AI: different product', unsure: 'AI: unsure' };
const IDENTITY_STYLE = {
  same: 'bg-emerald-50 text-emerald-800 border-emerald-300',
  different: 'bg-rose-50 text-rose-700 border-rose-300',
  unsure: 'bg-slate-50 text-slate-600 border-slate-300',
};
const BASIS_TEXT = { stated: 'stated in names', inferred: 'inferred, not stated', insufficient: 'insufficient evidence' };

export function AiVerdictChip({ verdict, testId }) {
  if (!verdict) return null;
  const id = IDENTITY_TEXT[verdict.identity] ? verdict.identity : 'unsure';
  return (
    <div className="flex items-start gap-1 mt-1 text-[10px]" data-testid={testId}>
      <span className={`inline-flex items-center gap-1 rounded border px-1.5 py-0.5 font-medium shrink-0 ${IDENTITY_STYLE[id]}`} data-testid={`${testId}-${id}`}>
        <Sparkles className="h-3 w-3" />{IDENTITY_TEXT[id]}
      </span>
      <span className="text-muted-foreground">({BASIS_TEXT[verdict.basis] || BASIS_TEXT.insufficient}{verdict.basis_note ? `: ${verdict.basis_note}` : ''}) {verdict.reason}</span>
    </div>
  );
}

export function AiCompareStatus({ state, onRun, testIdPrefix, disabled }) {
  const status = state?.status;
  const d = state?.data || {};
  return (
    <div className="flex items-center gap-2 flex-wrap text-[10px] text-muted-foreground" data-testid={`${testIdPrefix}-status`}>
      <Button size="sm" variant="outline" className="h-6 text-[10px] gap-1" onClick={onRun} disabled={disabled || status === 'loading'} data-testid={`${testIdPrefix}-run`}>
        {status === 'loading' ? <Loader2 className="h-3 w-3 animate-spin" /> : status ? <RefreshCw className="h-3 w-3" /> : <Sparkles className="h-3 w-3" />}
        {status ? 'Re-check with AI' : 'Compare with AI'}
      </Button>
      {status === 'loading' && <span>Comparing names…</span>}
      {status === 'ok' && (
        <span data-testid={`${testIdPrefix}-meta`}>
          AI assessment (advisory, not a probability) · {d.returned_model || d.model}{d.cached ? ' · cached' : ''}
          {d.no_suitable_candidate ? ' · AI found no matching candidate — search the full Product Master or leave unmatched' : ''}
        </span>
      )}
      {['error', 'rate_limited', 'disabled'].includes(status) && (
        <span className="text-rose-700" data-testid={`${testIdPrefix}-error`}>{d.message || 'AI comparison unavailable'} — ranked list and full search still work.</span>
      )}
    </div>
  );
}

const AI_ORDER = { same: 0, unsure: 1, undefined: 1, different: 2 };

// Physical (unit/form) conflicts stay last; within the rest, AI 'same' first, 'different' after.
export function sortWithAi(cands, verdicts, idKey, tierOf) {
  if (!verdicts) return cands;
  return [...cands].map((c, i) => ({ c, i })).sort((a, b) => {
    const pa = tierOf(a.c) === 2 ? 1 : 0;
    const pb = tierOf(b.c) === 2 ? 1 : 0;
    if (pa !== pb) return pa - pb;
    const va = AI_ORDER[verdicts[String(a.c[idKey])]?.identity];
    const vb = AI_ORDER[verdicts[String(b.c[idKey])]?.identity];
    return va !== vb ? va - vb : a.i - b.i;
  }).map((x) => x.c);
}
